import { useEffect, useId, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { AlertTriangleIcon, CheckIcon, XIcon } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { AnimalRow, AnimalTypeLabel } from '@/services/animal-management';

const CONVERSION_RULES: Record<AnimalTypeLabel, AnimalTypeLabel[]> = {
  Calf: ['Donor', 'LOPU Donor', 'Recipient'],
  Donor: ['Recipient'],
  Recipient: ['Donor'],
  'LOPU Donor': ['Calf', 'Donor', 'Recipient'],
};

type TypeFieldConfig = {
  field: 'opuProgram' | 'togId' | 'programCategory';
  labelKey: string;
  label: string;
};

const TYPE_REQUIRED_FIELDS: Record<AnimalTypeLabel, TypeFieldConfig[]> = {
  Donor: [
    {
      field: 'opuProgram',
      labelKey: 'animalManagement.columns.opuProgram',
      label: 'OPU Program',
    },
    {
      field: 'togId',
      labelKey: 'animalManagement.columns.togId',
      label: 'TOG ID',
    },
  ],
  Recipient: [
    {
      field: 'programCategory',
      labelKey: 'animalManagement.columns.programCategory',
      label: 'Program Category',
    },
  ],
  'LOPU Donor': [
    {
      field: 'opuProgram',
      labelKey: 'animalManagement.columns.opuProgram',
      label: 'OPU Program',
    },
    {
      field: 'programCategory',
      labelKey: 'animalManagement.columns.programCategory',
      label: 'Program Category',
    },
  ],
  Calf: [],
};

function isIrreversibleConversion(
  current: AnimalTypeLabel,
  target: AnimalTypeLabel,
): boolean {
  return !CONVERSION_RULES[target].includes(current);
}

const convertSchema = z
  .object({
    newType: z.enum(['Calf', 'LOPU Donor', 'Donor', 'Recipient']),
    opuProgram: z.string().default(''),
    togId: z.string().default(''),
    programCategory: z.string().default(''),
  })
  .superRefine((data, ctx) => {
    const required = TYPE_REQUIRED_FIELDS[data.newType];
    for (const { field } of required) {
      if (!data[field]?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Required',
          path: [field],
        });
      }
    }
  });

type ConvertFormValues = z.infer<typeof convertSchema>;

type ConvertTypeDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  animal: AnimalRow;
  onConvert: (
    newType: AnimalTypeLabel,
    fieldValues: Record<string, string>,
  ) => void;
};

export function ConvertTypeDialog({
  open,
  onOpenChange,
  animal,
  onConvert,
}: ConvertTypeDialogProps) {
  const { t } = useTranslation();
  const formId = useId();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingConversion, setPendingConversion] =
    useState<ConvertFormValues | null>(null);

  const allowedTargets = CONVERSION_RULES[animal.animalType];

  const form = useForm<ConvertFormValues>({
    resolver: zodResolver(convertSchema),
    defaultValues: {
      newType: animal.animalType,
      opuProgram: animal.opuProgram,
      togId: animal.togId,
      programCategory: animal.programCategory,
    },
  });

  const watchedNewType = form.watch('newType');
  const isNewTypeSelected = watchedNewType !== animal.animalType;
  const irreversible =
    isNewTypeSelected && watchedNewType
      ? isIrreversibleConversion(animal.animalType, watchedNewType)
      : false;
  const requiredFields =
    isNewTypeSelected && watchedNewType
      ? TYPE_REQUIRED_FIELDS[watchedNewType]
      : [];

  useEffect(() => {
    if (open) {
      form.reset({
        newType: animal.animalType,
        opuProgram: animal.opuProgram,
        togId: animal.togId,
        programCategory: animal.programCategory,
      });
      setShowConfirmation(false);
      setPendingConversion(null);
    }
  }, [open, animal, form, allowedTargets]);

  const handleFormSubmit = (data: ConvertFormValues) => {
    if (irreversible) {
      setPendingConversion(data);
      setShowConfirmation(true);
    } else {
      const fieldValues: Record<string, string> = {};
      for (const { field } of TYPE_REQUIRED_FIELDS[data.newType]) {
        fieldValues[field] = data[field];
      }
      onConvert(data.newType, fieldValues);
      onOpenChange(false);
    }
  };

  const handleConfirmConversion = () => {
    if (!pendingConversion) return;
    const fieldValues: Record<string, string> = {};
    for (const { field } of TYPE_REQUIRED_FIELDS[pendingConversion.newType]) {
      fieldValues[field] = pendingConversion[field];
    }
    onConvert(pendingConversion.newType, fieldValues);
    setShowConfirmation(false);
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange} modal>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t(
                'animalManagement.convertAnimalType.title',
                'Convert Animal Type',
              )}
            </DialogTitle>
            <DialogDescription>
              {t('animalManagement.convertAnimalType.subtitle', {
                defaultValue:
                  'Select the new type for {nameTag} and fill in required fields',
                nameTag: animal.nameTag,
              })}
            </DialogDescription>
          </DialogHeader>

          <DialogBody
            as="form"
            id={formId}
            onSubmit={form.handleSubmit(handleFormSubmit)}
            className="flex flex-col gap-4"
          >
            <div className="grid grid-cols-2 gap-4">
              <Field>
                <FieldLabel>
                  {t(
                    'animalManagement.convertAnimalType.currentType',
                    'Current Type:',
                  )}
                </FieldLabel>
                <Input value={animal.animalType} disabled />
              </Field>

              <Controller
                name="newType"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel>
                      {t(
                        'animalManagement.convertAnimalType.newType',
                        'New Type:',
                      )}
                    </FieldLabel>
                    <Select {...field}>
                      <SelectTrigger
                        aria-invalid={fieldState.invalid}
                        className="w-full"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={animal.animalType}>
                          {animal.animalType} (Current)
                        </SelectItem>
                        {allowedTargets.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {fieldState.invalid && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                )}
              />
            </div>

            {irreversible && (
              <div className="flex items-start gap-2 rounded-lg border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800">
                <AlertTriangleIcon className="mt-0.5 size-4 shrink-0" />
                <span>
                  {t(
                    'animalManagement.convertAnimalType.irreversibleWarning',
                    'This conversion requires confirmation and cannot be undone.',
                  )}
                </span>
              </div>
            )}

            {isNewTypeSelected && watchedNewType && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <div className="text-sm font-semibold">
                  {t(
                    'animalManagement.convertAnimalType.typeSpecificFieldsTitle',
                    {
                      defaultValue: '{type} Specific Fields',
                      type: watchedNewType,
                    },
                  )}
                </div>
                <p className="text-muted-foreground mb-3 text-sm">
                  {t(
                    'animalManagement.convertAnimalType.typeSpecificFieldsHelper',
                    'Fill in the required fields for the new animal type before converting.',
                  )}
                </p>
                {requiredFields.length > 0 && (
                  <div className="flex flex-col gap-3">
                    {requiredFields.map(({ field, labelKey, label }) => (
                      <Controller
                        key={field}
                        name={field}
                        control={form.control}
                        render={({ field: formField, fieldState }) => (
                          <Field data-invalid={fieldState.invalid}>
                            <FieldLabel>
                              {t(labelKey, label)}*{' '}
                              {fieldState.invalid && (
                                <span className="text-red-500">Required</span>
                              )}
                            </FieldLabel>
                            <Input
                              {...formField}
                              aria-invalid={fieldState.invalid}
                            />
                            {fieldState.invalid && (
                              <FieldError errors={[fieldState.error]} />
                            )}
                          </Field>
                        )}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </DialogBody>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              <XIcon className="mr-2 size-4" />
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" form={formId} disabled={!isNewTypeSelected}>
              <CheckIcon className="mr-2 size-4" />
              {irreversible
                ? t('animalManagement.convertAnimalType.continue', 'Continue')
                : t('animalManagement.convertAnimalType.convert', 'Convert')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showConfirmation}
        onOpenChange={(open) => {
          if (!open) setShowConfirmation(false);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangleIcon className="size-5" />
              {t(
                'animalManagement.convertAnimalType.confirmationTitle',
                'Irreversible Conversion Warning',
              )}
            </DialogTitle>
            <DialogDescription>
              {t(
                'animalManagement.convertAnimalType.confirmationSubtitle',
                'This conversion cannot be undone.',
              )}
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="flex flex-col gap-4">
            <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-4 text-sm">
              <p className="mb-3">
                You are about to convert <strong>{animal.nameTag}</strong> from{' '}
                <strong>{animal.animalType}</strong> to{' '}
                <strong>{pendingConversion?.newType}</strong>.
              </p>
              <p className="mb-2">
                {t(
                  'animalManagement.convertAnimalType.confirmationAware',
                  'Are you aware that:',
                )}
              </p>
              <ul className="list-disc space-y-1 pl-5">
                <li>
                  This change <strong>cannot be undone</strong>
                </li>
                <li>
                  {t('animalManagement.convertAnimalType.confirmationLocked', {
                    defaultValue:
                      'Original {fromType}-specific data will be locked',
                    fromType: animal.animalType,
                  })}
                </li>
                <li>
                  {t(
                    'animalManagement.convertAnimalType.confirmationReadOnly',
                    'Locked fields will be grayed out and read-only',
                  )}
                </li>
                <li>
                  {t(
                    'animalManagement.convertAnimalType.confirmationAudit',
                    'An audit log entry will be created with timestamp and user',
                  )}
                </li>
              </ul>
            </div>

            {pendingConversion && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm">
                <p className="mb-1 font-semibold">
                  {t(
                    'animalManagement.convertAnimalType.newTypeFields',
                    'New Type Fields:',
                  )}
                </p>
                <ul className="list-disc pl-5">
                  {TYPE_REQUIRED_FIELDS[pendingConversion.newType].map(
                    ({ field, labelKey, label }) => (
                      <li key={field}>
                        {t(labelKey, label)}: {pendingConversion[field]}
                      </li>
                    ),
                  )}
                </ul>
              </div>
            )}
          </DialogBody>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowConfirmation(false)}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              type="button"
              className="bg-amber-600 hover:bg-amber-700"
              onClick={handleConfirmConversion}
            >
              {t(
                'animalManagement.convertAnimalType.proceedWithConversion',
                'Proceed with Conversion',
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
